import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { datadogLogs } from '@datadog/browser-logs';
import { faMicrophoneSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerContainer from 'src/components/drawers/DrawerContainer';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import { AutoOpenDrawer } from 'src/constants/AutoOpenDrawers';
import usePageVisibility from 'src/hooks/usePageVisibility';
import useUserPermissions from 'src/hooks/useUserPermissions';
import { selectFacilityId } from 'src/store/configSlice';
import {
    dismissAutoOpenDrawer,
    requestAutoOpenDrawer,
    selectActiveAutoOpenDrawer,
} from 'src/store/globalUISlice';
import { useGetSpeakersQuery } from 'src/store/sageAdminApi';
import { snackActions } from 'src/utils/snackBarTools';

export const AnalyticsPrefix = 'speaker';

const isMicSupported =
    typeof window !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';

const canQueryMicPermission =
    isMicSupported && typeof navigator !== 'undefined' && 'permissions' in navigator;

export type MicPermissionContextValuesType = {
    isMicSupported: boolean;
    micPermission?: PermissionState;
    requestMicPermissionPopup: () => Promise<boolean>;
};

function formatGetUserMediaError(error: Error | undefined): string {
    switch (error?.name) {
        case 'NotAllowedError':
            return 'Microphone access denied. Open your browser settings and allow access.';
        case 'NotFoundError':
            return 'No microphone detected. Microphone required for Voice Triage.';
        case 'NotReadableError':
            return 'Microphone in use by another app. Quit that app and try again.';
        default:
            return 'Microphone unavailable. Try again or check your browser settings.';
    }
}

export const MicPermissionContext = createContext<MicPermissionContextValuesType>({
    isMicSupported: false,
    micPermission: undefined,
    requestMicPermissionPopup: () => Promise.resolve(false),
});

function MicPermissionContextProvider(props: PropsWithChildren) {
    const { palette } = useTheme();

    const { trackEvent } = useAnalytics();
    const facilityId = useSelector(selectFacilityId);
    const activeAutoOpenDrawer = useSelector(selectActiveAutoOpenDrawer);
    const dispatch = useDispatch();

    const [micPermission, setMicPermission] = useState<PermissionState | undefined>();
    const [hasAutoPrompted, setHasAutoPrompted] = useState(false);

    const isMicDrawerOpen = activeAutoOpenDrawer === AutoOpenDrawer.Microphone;

    const { data: speakers = [], isLoading: isSpeakersLoading } = useGetSpeakersQuery(
        { facilityRid: facilityId },
        { skip: !facilityId }
    );

    const hasSpeakersAtFacility = useMemo(() => speakers.length > 0, [speakers]);

    const { isResponder } = useUserPermissions();

    const isVisible = usePageVisibility();

    // hook for keeping the context in sync with the browser:
    // read the current state on mount, then listen for grants/denials made
    // outside the app (e.g. via site settings).
    useEffect(() => {
        if (!canQueryMicPermission || !isVisible) return;

        let status: PermissionStatus | null = null;
        let cancelled = false;
        const handleChange = () => status && setMicPermission(status.state);

        const subscribeToBrowserMicPermissions = async () => {
            try {
                const permissionStatus = await navigator.permissions.query({
                    name: 'microphone' as PermissionName,
                });

                // Effect torn down while query was in flight — don't attach event listener
                if (cancelled) return;

                // initialize state and listen for future grants/denials
                status = permissionStatus;
                setMicPermission(permissionStatus.state);
                permissionStatus.addEventListener('change', handleChange);
            } catch (error: any) {
                datadogLogs.logger.warn('Failed to query microphone permission state', {
                    error: error?.message,
                    name: error?.name,
                });
            }
        };

        subscribeToBrowserMicPermissions();

        // Cancel any in-flight attach + remove the listener if it landed
        return () => {
            cancelled = true;
            status?.removeEventListener('change', handleChange);
        };
    }, [isVisible]);

    useEffect(() => {
        setHasAutoPrompted(false);
    }, [facilityId]);

    // Auto-open the drawer once per session when this facility has speakers
    // and the browser hasn't responded to request for mic permission.
    useEffect(() => {
        if (
            !isMicSupported ||
            !facilityId ||
            isSpeakersLoading ||
            !hasSpeakersAtFacility ||
            !isResponder ||
            !micPermission ||
            micPermission !== 'prompt' ||
            hasAutoPrompted
        ) {
            return;
        }
        dispatch(requestAutoOpenDrawer(AutoOpenDrawer.Microphone));
        setHasAutoPrompted(true);
    }, [
        dispatch,
        facilityId,
        isSpeakersLoading,
        hasSpeakersAtFacility,
        isResponder,
        micPermission,
        hasAutoPrompted,
    ]);

    const requestMicPermissionPopup = useCallback(async (): Promise<boolean> => {
        if (!isMicSupported) return false;

        trackEvent('Microphone permission request acknowledged', {
            objectType: ObjectTypes.Dialog,
            permissionResult: micPermission,
        });

        if (micPermission === 'granted') return true;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // getUserMedia opens up and serves an audio stream (even before we
            // want it to when connecting to a device). In order to fetch perms,
            // we have to get the media and then stop the audio stream that initiates
            // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
            stream.getTracks().forEach((track) => track.stop());

            setMicPermission('granted');
            datadogLogs.logger.info('Microphone permission granted');

            return true;
        } catch (error: any) {
            snackActions.error(formatGetUserMediaError(error));
            setMicPermission('denied');
            datadogLogs.logger.warn('Microphone permission denied', {
                error: error?.message,
                name: error?.name,
            });
            return false;
        }
    }, [micPermission, trackEvent]);

    const handleConfirm = useCallback(async () => {
        trackEvent(`${AnalyticsPrefix} Mic Permission Request Acknowledged`, {
            objectType: ObjectTypes.Dialog,
        });
        await requestMicPermissionPopup();
        dispatch(dismissAutoOpenDrawer(AutoOpenDrawer.Microphone));
    }, [dispatch, requestMicPermissionPopup, trackEvent]);

    const value = useMemo(
        () => ({ isMicSupported, micPermission, requestMicPermissionPopup }),
        [micPermission, requestMicPermissionPopup]
    );

    return (
        <MicPermissionContext.Provider value={value}>
            {props.children}
            <DrawerContainer
                anchor="bottom"
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        // we want this to explicitly be controlled by the `open` prop
                        // see https://mui.com/material-ui/migration/v5-component-changes/#%E2%9C%85-remove-disablebackdropclick-prop
                        return;
                    }
                }}
                open={isMicDrawerOpen}
            >
                <DrawerBody sx={{ alignItems: 'flex-start', gap: 2, pt: 3, pb: 2, px: 2 }}>
                    <Avatar className="large">
                        <FontAwesomeIcon icon={faMicrophoneSlash} color={palette.primary.main} />
                    </Avatar>
                    <Stack sx={{ alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            Allow microphone access
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Activating your microphone will allow you to speak with residents
                            through their unit speaker during calls.
                        </Typography>
                    </Stack>
                    <Stack sx={{ py: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            NOTE
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ py: 0.25 }}>
                            Your microphone will only be on during active calls. It will not capture
                            audio any other time.
                        </Typography>
                    </Stack>
                </DrawerBody>

                <DrawerFooter sx={{ gap: 2, p: 2 }}>
                    <Button disableElevation fullWidth variant="contained" onClick={handleConfirm}>
                        Allow microphone
                    </Button>
                    <Button
                        disableElevation
                        fullWidth
                        variant="text"
                        onClick={() => dispatch(dismissAutoOpenDrawer(AutoOpenDrawer.Microphone))}
                    >
                        Skip for now
                    </Button>
                </DrawerFooter>
            </DrawerContainer>
        </MicPermissionContext.Provider>
    );
}

export default MicPermissionContextProvider;
